import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from "@angular/material/icon";
import { MLPredict, MLPredictDataInput, PredictionGroup } from '../../../interfaces/predictModalLoad';
import { PredictService } from '../../../services/predict.service';
import { FormsModule } from '@angular/forms';
import { formatDateOnly } from '../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { group } from 'console';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-predict-schedule-model',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatTableModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    DragDropModule
],
  templateUrl: './predict-schedule-model.component.html',
  styleUrl: './predict-schedule-model.component.scss'
})
export class PredictScheduleModelComponent implements OnInit {
  showPredictedGas2 : boolean = false;
  predictionData: MLPredict[][] = [];
  /* Data prepared specifically for displaying the HTML table.*/
  predictionGroups: PredictionGroup[] = [];
  selectedGroup: PredictionGroup| null = null;
  predictionDate: string = '';
  siteId: number = 0;
  fromDate: Date = new Date();
  toDate: Date = new Date();
  startDate: Date = new Date();
  constructor(public dialogRef: MatDialogRef<PredictScheduleModelComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MLPredictDataInput,
    private predictService: PredictService){
      console.log("incoming data", data)
      this.siteId = data.siteId;
      this.fromDate = data.fDate;
      this.toDate = data.tDate;
      this.startDate = data.fDate;
      this.showPredictedGas2 = data.siteId === 6;
  }

  ngOnInit(): void {
    this.loadPredictData(this.fromDate, this.toDate);
  }

  loadPredictData(startDate: Date, endDate: Date){
    let start = formatDateOnly(startDate) ?? '';
    let end = formatDateOnly(endDate) ?? '';
    this.predictService.getMLPredictSchedule(this.siteId, start, end).subscribe((result: MLPredict[][]) =>{
      this.predictionData = result;
      console.log("dat in grid", result);
      this.buildPredictionGroups();
    })
  }

  get hasSelectedGroups(): boolean {
    return this.predictionGroups.some(group => group.selected);
  }

  buildPredictionGroups(): void {

    this.predictionGroups = this.predictionData
      .filter(group => group && group.length > 0)
      .map((group: MLPredict[], index: number) => {

        const predictionRecord = group[group.length - 1];
        const streamIds = group.map(item => item.streamId).filter(id => id !== null && id !== undefined).join(',');
        const totalTipFee = group.reduce((total, item) => total + (Number(item.tipFee) || 0), 0);
        const nextDateValue = group.map(item => item.nextDate).find(value => !!value);

        return {
          groupIndex: index,
          selected: false,
          streams: group,
          // These values are shown only once for the entire group.
          predictedGas: predictionRecord.mcfD1 ?? 0,
          predictedGas2: predictionRecord.mcfD2 ?? 0,
          fat: predictionRecord.fat ?? 0,
          carbs: predictionRecord.carbs ?? 0,
          protein: predictionRecord.protein ?? 0,
          // Sum Tip Fee from every stream in this group.
          totalTipFee: totalTipFee,
          nextDate: nextDateValue ?? '',
          streamIds
        };
      });
    this.selectedGroup = null;
    console.log('Prediction groups prepared for dialog:', this.predictionGroups);
  }

  onGroupSelectionChange(selectedRow: PredictionGroup, isSelected: boolean): void {
    if (isSelected) {
      // Unselect every other prediction option.
      this.predictionGroups.forEach(group => {
        group.selected = group.groupIndex === selectedRow.groupIndex;
      });
      this.selectedGroup = selectedRow;
    } else {
      selectedRow.selected = false;
      this.selectedGroup = null;
    }

    console.log('Selected prediction group:', this.selectedGroup);
  }

  get canConfirm(): boolean {
    return this.selectedGroup !== null;
  }

  get isPreviousDayDisabled(): boolean {
    return this.isSameDate(this.startDate, this.fromDate);
  }

  get isNextDayDisabled(): boolean {
    return this.isSameDate(this.startDate, this.toDate);
  }

  private isSameDate(date1: Date, date2: Date): boolean {

    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }

  onConfirm(): void {

    if (!this.selectedGroup) {
      return;
    }

    const group = this.selectedGroup;

    let mcf = Number(group.predictedGas) || 0;

    if (this.showPredictedGas2) {
      mcf += Number(group.predictedGas2) || 0;
    }

    const nextDate = this.predictionGroups.length > 0 ? this.predictionGroups[0].nextDate : '';

    const predictionDate = formatDateOnly(nextDate) ?? '';

    console.log('Confirming prediction:', {
      siteId: this.siteId,
      scheduleDate: predictionDate,
      streamIds: group.streamIds,
      mcf: mcf
    });

    this.predictService.saveMLPredictFeedstockSchedule(this.data.siteId, predictionDate, group.streamIds, mcf)
      .subscribe({
        next: (responses) =>{
          console.log('Prediction groups saved successfully:', responses);
          this.dialogRef.close({
            confirmed: true,
            selectedRows: this.selectedGroup,
            responses
          });
        }
      })
  }

  onPreviousDay(): void {

    if (this.isPreviousDayDisabled) {
      return;
    }

    const previousDate = new Date(this.startDate);
    previousDate.setDate(previousDate.getDate() - 1);

    this.startDate = previousDate;

    // Clear previous selection.
    this.selectedGroup = null;

    this.predictionGroups.forEach(group => {
      group.selected = false;
    });

    this.loadPredictData(this.startDate, this.toDate);
  }

  onNextDay(): void {
    if (this.isNextDayDisabled) {
      return;
    }
  
    const nextDate = new Date(this.startDate);
    nextDate.setDate(nextDate.getDate() + 1);
  
    this.startDate = nextDate;
    
    this.selectedGroup = null;
    this.predictionGroups.forEach(group => {
      group.selected = false;
    });
    
    this.loadPredictData(nextDate, this.toDate);
  }
  
  onCancel(): void {
    this.dialogRef.close(null);
  }

  trackByGroupIndex(index: number, group: PredictionGroup): number {
    return group.groupIndex;
  }

  trackByStream(index: number, stream: MLPredict): string {
    return `${stream.streamId}-${stream.groupNumber}-${index}`;
  }
}
